import { isFunction, map, filter, extend, omit, identity, range, isEmpty } from "lodash";
import React, { useMemo } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import Table from "antd/lib/table";
import Skeleton from "antd/lib/skeleton";
import FavoritesControl from "@/components/favorites-control/FavoritesControl";
import TimeAgo from "@/components/TimeAgo";
import { durationHumanize, formatDate, formatDateTime } from "@/lib/utils";
import './ItemsTable.less'
import { useTheme } from "@/components/ThemeProvider";

// `this` refers to previous function in the chain (`Columns.***`).
// Adds `sorter: true` field to column definition
function sortable(...args) {
  return extend(this(...args), { sorter: true });
}

const WrappableExpandableContent = ({ children, collapsedHeight = "20px" }) => {
  const [isExpanded, setIsExpanded] = React.useState(false);

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div className="centered-wrappable-expandable-container">
      <span
        className="expand-toggle"
        onClick={toggleExpand}
        style={{
          cursor: 'pointer',
          color: '#787879',
          marginRight: '10px',
          display: 'inline-block',
          transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
          transition: 'transform 0.2s',
          position: "absolute", 
          left: 0,
          top: '50%',
        }}
      >
        ▶
      </span>
      <div
        className={classNames("wrappable-content", { expanded: isExpanded })}
        style={{
          maxHeight: isExpanded ? "none" : collapsedHeight, // Collapse height when not expanded
          overflow: isExpanded ? "visible" : "hidden", // Hide overflow when collapsed
          textOverflow: 'ellipsis', // Show ellipsis when collapsed
          whiteSpace: isExpanded ? 'normal' : 'nowrap', // Allow wrapping when expanded
          transition: "max-height 0.3s ease-in-out", // Smooth transition for expanding/collapsing
          paddingLeft: '20px', // Offset the content to make space for the arrow
        }}
      >
        {children}
      </div>
    </div>
  );
};

export const Columns = {
  favorites(overrides) {
    return extend(
      {
        width: "1%",
        render: (text, item) => <FavoritesControl item={item} />,
      },
      overrides
    );
  },
  avatar(overrides, formatTitle) {
    formatTitle = isFunction(formatTitle) ? formatTitle : identity;
    return extend(
      {
        width: "1%",
        render: (user, item) => (
          <img
            src={item.user.profile_image_url}
            className="profile__image_thumb"
            alt={formatTitle(user.name, item)}
            title={formatTitle(user.name, item)}
          />
        ),
      },
      overrides
    );
  },
  date(overrides) {
    return extend(
      {
        render: text => formatDate(text),
      },
      overrides
    );
  },
  dateTime(overrides) {
    return extend(
      {
        render: text => formatDateTime(text),
      },
      overrides
    );
  },
  duration(overrides) {
    return extend(
      {
        width: "1%",
        className: "text-nowrap",
        render: text => durationHumanize(text),
      },
      overrides
    );
  },
  timeAgo(overrides, timeAgoCustomProps = undefined) {
    return extend(
      {
        render: value => <TimeAgo date={value} {...timeAgoCustomProps} />,
      },
      overrides
    );
  },
  custom(render, overrides) {
    return extend(
      {
        render,
      },
      overrides
    );
  },
  centered(render, overrides) {
    return extend(
      {
        align: "center",
        render: (text, record) => (
          <div style={{ textAlign: "left" }}> {/* Ensure the cell content is left-aligned */}
            {render(text, record)}
          </div>
        ),
      },
      overrides
    );
  },
  centeredWrappableExpandable(render, overrides) {
    return extend(
      {
        align: "center", // Center the header title
        render: (text, record) => (
          <div style={{ textAlign: "left" }}> 
            <WrappableExpandableContent collapsedHeight="50px">
              {render(text, record)}
            </WrappableExpandableContent>
          </div>
        ),
      },
      overrides
    );
  }
}

Columns.date.sortable = sortable;
Columns.dateTime.sortable = sortable;
Columns.duration.sortable = sortable;
Columns.timeAgo.sortable = sortable;
Columns.custom.sortable = sortable;
Columns.centered.sortable = sortable;

const ItemsTable = ({
  loading,
  items,
  columns,
  showHeader,
  onRowClick,
  orderByField,
  orderByReverse,
  toggleSorting,
  "data-test": dataTest,
  rowKey,
}) => {
  const { isDarkMode } = useTheme();

  const prepareColumns = useMemo(() => {
    const orderByDirection = orderByReverse ? "descend" : "ascend";

    return map(
      map(
        filter(columns, column => (isFunction(column.isAvailable) ? column.isAvailable() : true)),
        column => extend(column, { orderByField: column.orderByField || column.field })
      ),
      (column, index) => {
        const onHeaderCell = column.sorter ? () => ({ onClick: () => toggleSorting(column.orderByField) }) : null;
        const render = isFunction(column.render) ? (text, row) => column.render(text, row.item) : identity;

        return extend(omit(column, ["field", "orderByField", "render"]), {
          key: "column" + index,
          dataIndex: ["item", column.field],
          defaultSortOrder: column.orderByField === orderByField ? orderByDirection : null,
          onHeaderCell,
          render,
        });
      }
    );
  }, [columns, orderByField, orderByReverse, toggleSorting]);

  const getRowKey = record => {
    if (rowKey) {
      if (isFunction(rowKey)) {
        return rowKey(record.item);
      }
      return record.item[rowKey];
    }
    return record.key;
  };

  const tableDataProps = {
    columns: prepareColumns,
    dataSource: map(items, (item, index) => ({ key: "row" + index, item })),
  };

  const onTableRow = isFunction(onRowClick)
    ? row => ({
        onClick: event => {
          onRowClick(event, row.item);
        },
      })
    : null;

  if (loading) {
    if (isEmpty(tableDataProps.dataSource)) {
      tableDataProps.columns = tableDataProps.columns.map(column => ({
        ...column,
        sorter: false,
        render: () => <Skeleton active paragraph={false} />,
      }));
      tableDataProps.dataSource = range(10).map(key => ({ key: `${key}` }));
    } else {
      tableDataProps.loading = { indicator: null };
    }
  }

  return (
    <Table
      className={classNames("table-data", { "ant-table-headerless": !showHeader, "dark-mode": isDarkMode })}
      showHeader={showHeader}
      rowKey={getRowKey}
      pagination={false}
      onRow={onTableRow}
      data-test={dataTest}
      {...tableDataProps}
    />
  );
};

ItemsTable.propTypes = {
  loading: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  items: PropTypes.arrayOf(PropTypes.object),
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      field: PropTypes.string, // data field
      orderByField: PropTypes.string, // field to order by (defaults to `field`)
      render: PropTypes.func, // (prop, item) => text | node; `prop` is `item[field]`
      isAvailable: PropTypes.func, // return `true` to show column and `false` to hide; if omitted: show column
    })
  ),
  showHeader: PropTypes.bool,
  onRowClick: PropTypes.func, // (event, item) => void

  orderByField: PropTypes.string,
  orderByReverse: PropTypes.bool,
  toggleSorting: PropTypes.func,
  "data-test": PropTypes.string,
  rowKey: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
};

ItemsTable.defaultProps = {
  loading: false,
  items: [],
  columns: [],
  showHeader: true,
  onRowClick: null,

  orderByField: null,
  orderByReverse: false,
  toggleSorting: () => {},
};

export default ItemsTable;
